// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type WorkCellId, type WorkCellTaskKanban, useEvolvePatch, type Facility } from '@evolvemep/api-ts';
import { Flex, Space, Text } from '@mantine/core';

import { CustomPropertiesPopover } from 'components/Mantine/CustomProperties/CustomPropertiesPopover';
import { KanbanCard } from 'components/Mantine/Kanban/KanbanCard';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { isNotNil } from 'helpers/isNotNil';
import { ActionTaskMenu } from 'modules/Shop/Tasks/ActionTaskMenu';
import { TaskPlayPauseIcon } from 'modules/Shop/Tasks/TaskPlayPauseIcon';
import { WorkCellTaskLink } from 'modules/Shop/Tasks/WorkCellTaskLink';
import { PredecessorsBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/PredecessorsBadge';

type Props = {
  workCellTaskKanban: WorkCellTaskKanban;
  onTaskMoved: (newWorkCellId: WorkCellId) => void;
  index: number;
  selectedFacility: Facility;
};

export const WorkCellsTaskKanbanCard = ({
  workCellTaskKanban: initialWorkCellTaskKanban,
  onTaskMoved,
  index,
  selectedFacility,
}: Props) => {
  const [workCellTaskKanban, setWorkCellTaskKanban] = useState(initialWorkCellTaskKanban);

  const { apiCall: patchTaskApiCall, loading: saving } = useEvolvePatch(`shop/task/${workCellTaskKanban.taskId}`);
  return (
    <KanbanCard
      id={workCellTaskKanban.taskId}
      index={index}
      currentPage="Tasks"
      cardDestination={`/task-viewer/${workCellTaskKanban.taskId}`}
      {...workCellTaskKanban}
      customStatusEntityName="Task"
      stateName={workCellTaskKanban.taskStatusTypeName}
      cardProps={{
        style: {
          cursor: 'pointer',
        },
      }}
      badges={
        <CustomPropertiesPopover
          size="lg"
          entity={workCellTaskKanban}
          disabled={saving}
          customPropertyTypeName="Task"
          onUpdateCustomProperty={(customPropertyId, value) =>
            patchTaskApiCall({
              customProperties: [{ customPropertyId, value }],
            }).then(({ customProperties }) =>
              setWorkCellTaskKanban((wct) => ({
                ...wct,
                customProperties,
              })),
            )
          }
        />
      }
      footer={
        <>
          <PredecessorsBadge task={workCellTaskKanban} />
          <Flex flex={1} justify="end">
            <ActionTaskMenu
              task={workCellTaskKanban}
              onTaskUpdated={(task) => {
                setWorkCellTaskKanban((wct) => ({
                  ...wct,
                  ...task,
                }));
                if (isNotNil(task.workCellId)) {
                  onTaskMoved(task.workCellId);
                }
              }}
              parentPage="Tasks"
              facility={selectedFacility}
            />
          </Flex>
        </>
      }
    >
      <WorkCellTaskLink
        task={workCellTaskKanban}
        from="Tasks"
        linkProps={{
          className: 'kanban-work-order-link',
        }}
      >
        <Text lineClamp={2} fw={500}>
          <TaskPlayPauseIcon
            taskStatusTypeName={workCellTaskKanban.taskStatusTypeName}
            spanProps={{ style: { marginTop: 1, marginRight: 3, marginLeft: -3 } }}
          />
          {workCellTaskKanban.taskName}
        </Text>
      </WorkCellTaskLink>
      <Space />
      <Text lineClamp={2} fz="sm">
        {workCellTaskKanban.workOrderItemName}
      </Text>
      <EvolveLink className="kanban-work-order-link" to={`/work-orders/${workCellTaskKanban.workOrderId}`} from="Tasks">
        <Text display="inline" lineClamp={2} fz="sm">
          {workCellTaskKanban.workOrderName}
        </Text>
      </EvolveLink>
    </KanbanCard>
  );
};
